import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Settings as SettingsIcon } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useCounters } from "../../contexts/CountersContext";
import { StatusChip, STATUS_LABELS, fmtDateTime } from "./_shared";

function useDebounce(v, d = 300) {
    const [x, setX] = useState(v);
    useEffect(() => { const t = setTimeout(() => setX(v), d); return () => clearTimeout(t); }, [v, d]);
    return x;
}

function CategoriesModal({ open, onClose, onChanged }) {
    const [items, setItems] = useState([]);
    const [name, setName] = useState("");
    const [editing, setEditing] = useState({});
    const reload = async () => {
        const { data } = await api.get("/internal/ticket-categories", { params: { active: "all" } });
        setItems(data.items || []);
    };
    useEffect(() => { if (open) reload(); }, [open]);
    if (!open) return null;
    const add = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try { await api.post("/internal/ticket-categories", { name: name.trim() }); setName(""); reload(); onChanged?.(); }
        catch (er) { toast.error(er?.response?.data?.detail || "Errore"); }
    };
    const save = async (id, patch) => {
        try { await api.patch(`/internal/ticket-categories/${id}`, patch); reload(); onChanged?.(); }
        catch (er) { toast.error(er?.response?.data?.detail || "Errore"); }
    };
    return (
        <div className="lt-modal-overlay" data-testid="cat-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="lt-modal" style={{ maxWidth: 520 }}>
                <h2 className="lt-section-title" style={{ marginBottom: 12 }}>Categorie ticket</h2>
                <form onSubmit={add} style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    <input className="lt-field" style={{ marginBottom: 0, flex: 1 }} placeholder="Nuova categoria…" value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-new-name" />
                    <button className="lt-btn-primary" type="submit" data-testid="cat-add">Aggiungi</button>
                </form>
                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    {items.map((c) => (
                        <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #EEEFEA" }} data-testid={`cat-row-${c.id}`}>
                            <input className="lt-field" style={{ marginBottom: 0, flex: 1, height: 36 }}
                                defaultValue={c.name}
                                onBlur={(e) => e.target.value !== c.name && save(c.id, { name: e.target.value })} />
                            <span className={"lt-chip " + (c.active ? "lt-chip--active" : "lt-chip--inactive")}>{c.active ? "Attiva" : "Inattiva"}</span>
                            <button className="lt-btn-ghost" onClick={() => save(c.id, { active: !c.active })}>{c.active ? "Disattiva" : "Riattiva"}</button>
                        </div>
                    ))}
                    {items.length === 0 && <p style={{ color: "#8A8F88", fontSize: 13 }}>Nessuna categoria.</p>}
                </div>
                <div className="flex items-center justify-end" style={{ marginTop: 16 }}>
                    <button className="lt-btn-secondary" onClick={onClose}>Chiudi</button>
                </div>
            </div>
        </div>
    );
}

export default function TicketList() {
    const navigate = useNavigate();
    const { counters } = useCounters();
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [clientId, setClientId] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [assignee, setAssignee] = useState("");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [categories, setCategories] = useState([]);
    const [users, setUsers] = useState([]);
    const [catModal, setCatModal] = useState(false);
    const dq = useDebounce(q, 300);

    const counterCards = [
        { key: "aperto", label: "Aperti", value: counters?.aperti ?? 0 },
        { key: "in_lavorazione", label: "In lavorazione", value: counters?.in_lavorazione ?? 0 },
        { key: "in_attesa", label: "In attesa", value: counters?.in_attesa ?? 0 },
        { key: "chiuso", label: "Chiusi", value: counters?.chiusi ?? 0 },
    ];

    useEffect(() => {
        api.get("/internal/clients", { params: { status: "all", page_size: 200 } }).then(({ data }) => setClients(data.items || []));
        api.get("/internal/ticket-categories", { params: { active: "all" } }).then(({ data }) => setCategories(data.items || []));
        api.get("/internal/internal-users", { params: { active: true } }).then(({ data }) => setUsers(data.items || []));
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const params = { page: 1, page_size: 50 };
        if (dq) params.q = dq;
        if (status !== "all") params.status = status;
        if (clientId) params.client_id = clientId;
        if (categoryId) params.category_id = categoryId;
        if (assignee) params.assignee = assignee;
        try {
            const { data } = await api.get("/internal/tickets", { params });
            setItems(data.items || []); setTotal(data.total || 0);
        } catch { toast.error("Errore nel caricamento"); }
        finally { setLoading(false); }
    }, [dq, status, clientId, categoryId, assignee]);
    useEffect(() => { fetchData(); }, [fetchData]);

    return (
        <div style={{ padding: "32px 40px" }} data-testid="tickets-list-page">
            <div className="flex items-center justify-between" style={{ marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <h1 className="lt-h1" data-testid="tickets-heading">Ticket</h1>
                    <p className="lt-sub" style={{ marginTop: 6 }}>{total} {total === 1 ? "ticket" : "ticket"}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button className="lt-btn-secondary" onClick={() => setCatModal(true)} data-testid="tickets-manage-categories-btn">
                        <SettingsIcon size={14} /> Gestisci categorie
                    </button>
                    <button className="lt-btn-primary" onClick={() => navigate("/app/ticket/nuovo")} data-testid="tickets-new-btn">
                        <Plus size={16} strokeWidth={2.25} /> Nuovo ticket
                    </button>
                </div>
            </div>

            <div
                className="grid"
                style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16, marginBottom: 20 }}
                data-testid="tickets-counters"
            >
                {counterCards.map((c) => {
                    const isActive = status === c.key;
                    return (
                        <button
                            type="button"
                            key={c.key}
                            onClick={() => setStatus(isActive ? "all" : c.key)}
                            data-testid={`tickets-counter-${c.key}`}
                            className={"lt-counter-card" + (isActive ? " is-active" : "")}
                        >
                            <div className="lt-counter-card__value" data-testid={`tickets-counter-${c.key}-value`}>{c.value}</div>
                            <div className="lt-counter-card__label">{c.label}</div>
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center" style={{ gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
                <div style={{ position: "relative", width: 320 }}>
                    <Search size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#8A8F88" }} />
                    <input className="lt-field" style={{ marginBottom: 0, paddingLeft: 40 }} placeholder="Cerca titolo…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tickets-search" />
                </div>
                <select className="lt-field" style={{ marginBottom: 0, width: 180 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="tickets-filter-status">
                    <option value="all">Tutti gli stati</option>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select className="lt-field" style={{ marginBottom: 0, width: 200 }} value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="tickets-filter-client">
                    <option value="">Tutti i clienti</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
                <select className="lt-field" style={{ marginBottom: 0, width: 180 }} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="tickets-filter-category">
                    <option value="">Tutte le categorie</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select className="lt-field" style={{ marginBottom: 0, width: 200 }} value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="tickets-filter-assignee">
                    <option value="">Tutti gli assegnatari</option>
                    <option value="unassigned">Non assegnati</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
            </div>

            {loading ? (
                <div style={{ padding: 48, textAlign: "center", color: "#8A8F88" }}>Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="lt-card" style={{ padding: 48, textAlign: "center" }} data-testid="tickets-empty">
                    <p className="lt-sub">Nessun ticket.</p>
                </div>
            ) : (
                <div className="lt-table-wrap">
                    <table className="lt-table" data-testid="tickets-table">
                        <thead>
                            <tr>
                                <th style={{ width: 140 }}>Stato</th>
                                <th>Titolo</th>
                                <th>Cliente</th>
                                <th>Categoria</th>
                                <th>Assegnato a</th>
                                <th>Ultimo messaggio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((t) => (
                                <tr key={t.id} onClick={() => navigate(`/app/ticket/${t.id}`)}
                                    className={t.internal_unread ? "lt-row-unread" : ""}
                                    data-testid={`ticket-row-${t.id}`}>
                                    <td><StatusChip status={t.status} /></td>
                                    <td><span className="title">{t.title}</span></td>
                                    <td style={{ color: "#5F6560" }}>{t.client?.company_name || "—"}</td>
                                    <td style={{ color: "#5F6560" }}>{t.category?.name || "—"}</td>
                                    <td style={{ color: "#5F6560" }}>{t.assignee?.name || <span style={{ fontStyle: "italic" }}>non assegnato</span>}</td>
                                    <td style={{ color: "#5F6560" }}>{fmtDateTime(t.last_message_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CategoriesModal open={catModal} onClose={() => setCatModal(false)} onChanged={() => { api.get("/internal/ticket-categories", { params: { active: "all" } }).then(({ data }) => setCategories(data.items || [])); }} />
        </div>
    );
}
